import { useState, useEffect, type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import { 
  getTrailerById, 
  updateTrailer,
} from "../../../../../store/admin/trailersStore";
import { TrailerGridCard } from "../../../../admin/components/sections/trailers/components/TrailerGridCard";
import { TrailerDetailsModal } from "../../../../admin/components/sections/trailers/components/TrailerDetailsModal";
import { TrailerEditModal } from "../../../../admin/components/sections/trailers/components/TrailerEditModal";
import type { Trailer, TrailerFormData } from "../../../../admin/components/sections/trailers/types";
import { getThemeClasses } from "../../../../../utils/ThemeClasses";
import { useAlert } from "../../../../../context/AlertContext";
import { useTranslation } from "react-i18next";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";

interface TrailerContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

export default function TrailerContent({
  vehicle,
  isDarkMode,
}: TrailerContentProps) {
  const [trailer, setTrailer] = useState<Trailer | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [shipmentDetails, setShipmentDetails] = useState<string[]>([]);

  const { showAlert } = useAlert();
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();
  const themeClasses = getThemeClasses(isDarkMode);
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<TrailerFormData>();

  const watchedValues = watch();

  useEffect(() => {
    const fetchTrailer = async () => {
      if (vehicle.remolque_id) {
        setLoading(true);
        const result = await getTrailerById(vehicle.remolque_id);
        if (result.data) {
          setTrailer(result.data as unknown as Trailer);
        }
        setLoading(false);
      }
    };

    fetchTrailer();
  }, [vehicle.remolque_id]);

  const handleOpenEditModal = (trailerToEdit: Trailer) => {
    setValue("name", trailerToEdit.name);
    setValue("plate", trailerToEdit.plate);
    setValue("type", trailerToEdit.type);
    setValue("brand", trailerToEdit.brand);
    setValue("model", trailerToEdit.model);
    setValue("year", trailerToEdit.year);
    setValue("vin", trailerToEdit.vin);
    setValue("serialNumber", trailerToEdit.serialNumber);
    setValue("status_id", trailerToEdit.status_id || (trailerToEdit.status as any)?.id);
    setValue("condition", trailerToEdit.condition);
    setValue("description", trailerToEdit.description);
    setValue("capacity", trailerToEdit.capacity);
    setValue("maximumWeight", trailerToEdit.maximumWeight);
    setValue("axles", trailerToEdit.axles);
    setValue("length", trailerToEdit.length);
    setValue("width", trailerToEdit.width);
    setValue("height", trailerToEdit.height);
    setValue("brakeType", trailerToEdit.brakeType);
    setValue("suspensionType", trailerToEdit.suspensionType);
    setValue("registration", trailerToEdit.registration);
    setValue("insurance", trailerToEdit.insurance);
    setValue("permit", trailerToEdit.permit);
    setShipmentDetails(trailerToEdit.shipment_details || []);
    setImagePreview(trailerToEdit.image || null);
    setImageFile(null);
    setImageDeleted(false);
    setShowEditModal(true);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageDeleted(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setImagePreview(null);
    setImageFile(null);
    setImageDeleted(true);
  };

  const handleAddShipmentDetail = () => {
    setShipmentDetails([...shipmentDetails, ""]);
  };

  const handleRemoveShipmentDetail = (index: number) => {
    setShipmentDetails(shipmentDetails.filter((_, i) => i !== index));
  };

  const handleShipmentDetailChange = (index: number, value: string) => {
    const newDetails = [...shipmentDetails];
    newDetails[index] = value;
    setShipmentDetails(newDetails);
  };

  const onSubmitEdit = async (data: TrailerFormData) => {
    if (!trailer) return;

    setLoadingSubmit(true);

    const formData = new FormData();

    if (imageFile) {
      formData.append("imageFile", imageFile);
    } else if (imageDeleted) {
      formData.append("image", "null");
    }

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "" && key !== "image" && key !== "shipment_details") {
        formData.append(key, String(value));
      }
    });

    const filteredShipmentDetails = shipmentDetails.filter(detail => detail.trim() !== "");
    formData.append('shipment_details', JSON.stringify(filteredShipmentDetails));

    await updateTrailer(
      trailer.id,
      formData,
      showAlert,
      setLoadingSubmit,
      t,
      async () => {
        setShowEditModal(false);
        setIsRefreshing(true);
        if (vehicle.remolque_id) {
          const refreshResult = await getTrailerById(vehicle.remolque_id);
          if (refreshResult.success && refreshResult.data) {
            setTrailer(refreshResult.data as unknown as Trailer);
          }
        }
        setIsRefreshing(false);
      },
      reset
    );
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setImageFile(null);
    setImagePreview(null);
    setImageDeleted(false);
    setShipmentDetails([]);
    reset();
  };

  if (loading) {
    return (
      <div className={`p-3 border-t ${borderColor}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-caravan-line text-black text-xs"></i>
          </div>
          <h4 className={`font-semibold text-xs ${themeClasses.text}`}>
            Información del Remolque
          </h4>
        </div>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (!vehicle.remolque_id || !trailer) {
    return (
      <div className={`p-3 border-t ${borderColor}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-caravan-line text-black text-xs"></i>
          </div>
          <h4 className={`font-semibold text-xs ${themeClasses.text}`}>
            Información del Remolque
          </h4>
        </div>
        <div className={`text-center py-6 ${themeClasses.textSecondary}`}>
          <i className="ri-caravan-line text-2xl mb-2 block"></i>
          <p className="text-xs">Sin remolque asignado</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`p-3 border-t ${borderColor}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-caravan-line text-black text-xs"></i>
          </div>
          <h4 className={`font-semibold text-xs ${themeClasses.text}`}>
            Información del Remolque
          </h4>
        </div>

        <div className="relative">
          {isRefreshing && (
            <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                <span className="text-white text-xs">Actualizando...</span>
              </div>
            </div>
          )}

          <TrailerGridCard
            trailer={trailer}
            themeClasses={themeClasses}
            onEdit={handleOpenEditModal}
            onDetails={() => setShowDetailModal(true)}
            t={t}
            disableCRUD={disableCRUD}
          />
        </div>
      </div>

      {showDetailModal && trailer && (
        <TrailerDetailsModal
          trailer={trailer}
          themeClasses={themeClasses}
          onClose={() => setShowDetailModal(false)}
          onEdit={(trailerToEdit) => {
            setShowDetailModal(false);
            handleOpenEditModal(trailerToEdit);
          }}
          t={t}
        />
      )}

      {showEditModal && trailer && (
        <TrailerEditModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseEditModal}
          onSubmit={handleSubmit(onSubmitEdit)}
          onImageChange={handleImageChange}
          onImageDelete={handleImageDelete}
          imagePreview={imagePreview}
          t={t}
          setValue={setValue}
          watchedValues={watchedValues}
          shipmentDetails={shipmentDetails}
          onAddShipmentDetail={handleAddShipmentDetail}
          onRemoveShipmentDetail={handleRemoveShipmentDetail}
          onShipmentDetailChange={handleShipmentDetailChange}
        />
      )}
    </>
  );
}

